import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { hmacSha1 } from '../utils/cryptoUtils';

/**
 * واجهة إعدادات ImageKit.io
 */
export interface ImageKitSettings {
  publicKey: string;
  privateKey: string;
  urlEndpoint: string;
  enabled: boolean;
}

// الإعدادات الافتراضية
const DEFAULT_IMAGEKIT_SETTINGS: ImageKitSettings = {
  publicKey: '',
  privateKey: '',
  urlEndpoint: '',
  enabled: false
};

/**
 * الحصول على إعدادات ImageKit.io
 * @returns وعد بإعدادات ImageKit.io
 */
export const getImageKitSettings = async (): Promise<ImageKitSettings> => {
  try {
    // محاولة الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'imagekit'));

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as ImageKitSettings;
      return {
        publicKey: data.publicKey || '',
        privateKey: data.privateKey || '',
        urlEndpoint: data.urlEndpoint || '',
        enabled: data.enabled || false
      };
    }

    // إذا لم تكن الإعدادات موجودة، استخدم الإعدادات الافتراضية
    return DEFAULT_IMAGEKIT_SETTINGS;
  } catch (error) {
    console.error('Error getting ImageKit.io settings:', error);
    return DEFAULT_IMAGEKIT_SETTINGS;
  }
};

/**
 * حفظ إعدادات ImageKit.io
 * @param settings إعدادات ImageKit.io
 * @returns وعد بنتيجة عملية الحفظ
 */
export const saveImageKitSettings = async (settings: ImageKitSettings): Promise<void> => {
  try {
    await setDoc(doc(db, 'settings', 'imagekit'), {
      ...settings,
      updatedAt: new Date()
    });

    console.log('ImageKit.io settings saved successfully');
  } catch (error) {
    console.error('Error saving ImageKit.io settings:', error);
    throw error;
  }
};

/**
 * إنشاء توقيع محلي للاختبار (استخدام فقط عندما تفشل دالة Firebase Function)
 * @param token رمز عشوائي
 * @param expire وقت انتهاء الصلاحية
 * @returns بيانات المصادقة
 */
const generateLocalSignature = async (token: string, expire: number, privateKey: string): Promise<{ signature: string, token: string, expire: string }> => {
  console.log('Using local signature generation as fallback');
  
  // إنشاء سلسلة التوقيع
  const stringToSign = token + expire.toString();
  
  // إنشاء توقيع SHA-1 باستخدام المفتاح الخاص
  const signature = await hmacSha1(stringToSign, privateKey);
  
  console.log('Generated signature:', signature);
  
  return {
    signature,
    token,
    expire: expire.toString()
  };
};

/**
 * الحصول على بيانات المصادقة من Firebase Function
 * @param token رمز عشوائي
 * @param expire وقت انتهاء الصلاحية
 * @returns وعد ببيانات المصادقة
 */
const getAuthenticationParameters = async (token: string, expire: number): Promise<{ signature: string, token: string, expire: string }> => {
  try {
    console.log('Using local signature generation');
    const settings = await getImageKitSettings();
    
    if (!settings.privateKey) {
      throw new Error('المفتاح الخاص غير متوفر للتوقيع المحلي');
    }
    
    return await generateLocalSignature(token, expire, settings.privateKey);
  } catch (error) {
    console.error('Error generating authentication parameters:', error);
    throw error;
  }
};

/**
 * تحميل صورة إلى ImageKit.io باستخدام API الرسمي
 * @param file ملف الصورة المراد تحميله
 * @param folder المجلد (اختياري)
 * @returns وعد بعنوان URL الصورة
 */
export const uploadImageToImageKit = async (file: File, folder?: string): Promise<string> => {
  try {
    // الحصول على إعدادات ImageKit.io
    const settings = await getImageKitSettings();

    // التحقق من وجود إعدادات ImageKit.io
    if (!settings.publicKey || !settings.urlEndpoint || !settings.enabled) {
      console.error('ImageKit.io settings are incomplete or service is disabled');
      throw new Error('إعدادات ImageKit.io غير مكتملة أو الخدمة غير مفعلة');
    }

    console.log('تحميل الصورة إلى ImageKit.io...');

    // إنشاء اسم الملف
    const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;

    // إنشاء بيانات المصادقة
    const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const expire = Math.floor(Date.now() / 1000) + 3599; // أقل من ساعة
    
    // الحصول على التوقيع من نقطة النهاية للمصادقة
    const authData = await getAuthenticationParameters(token, expire);

    console.log('Uploading to ImageKit.io with the following parameters:');
    console.log('- Public Key:', settings.publicKey);
    console.log('- File Name:', fileName);
    console.log('- Token:', authData.token);
    console.log('- Expire:', authData.expire);
    console.log('- Signature:', authData.signature);

    // إنشاء نموذج FormData
    const formData = new FormData();
    formData.append('file', file); // إرسال الملف مباشرة بدلاً من Base64
    formData.append('publicKey', settings.publicKey);
    formData.append('signature', authData.signature);
    formData.append('token', authData.token);
    formData.append('expire', authData.expire);
    formData.append('fileName', fileName);
    formData.append('useUniqueFileName', 'true');

    if (folder) {
      formData.append('folder', folder);
    }

    // إرسال الطلب إلى ImageKit.io
    console.log('Sending request to ImageKit.io API...');

    const response = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
      method: 'POST',
      body: formData,
    });

    console.log('Response status:', response.status, response.statusText);

    // التحقق من نجاح الطلب
    if (!response.ok) {
      const errorText = await response.text();
      console.error('ImageKit.io API error response:', errorText);

      // محاولة تحليل رسالة الخطأ إذا كانت بتنسيق JSON
      try {
        const errorJson = JSON.parse(errorText);
        console.error('Parsed error:', errorJson);
        throw new Error(`فشل تحميل الصورة: ${errorJson.message || errorJson.error || 'خطأ غير معروف'}`);
      } catch (parseError) {
        // إذا لم يكن النص بتنسيق JSON، استخدم النص كما هو
        throw new Error(`فشل تحميل الصورة: ${response.status} ${response.statusText} - ${errorText}`);
      }
    }

    // الحصول على البيانات
    const data = await response.json();
    console.log('ImageKit.io API response:', data);

    if (!data.url) {
      console.error('ImageKit.io API response missing URL:', data);
      throw new Error('فشل تحميل الصورة: لم يتم العثور على عنوان URL في الاستجابة');
    }

    console.log('تم تحميل الصورة بنجاح إلى ImageKit.io:', data.url);
    return data.url;
  } catch (error) {
    console.error('Error uploading image to ImageKit.io:', error);
    throw error;
  }
};

/**
 * تحميل صورة إلى ImageKit.io باستخدام URL
 * @param imageUrl عنوان URL للصورة المراد تحميلها
 * @param folder المجلد (اختياري)
 * @returns وعد بعنوان URL الصورة الجديد
 */
export const uploadImageUrlToImageKit = async (imageUrl: string, folder?: string): Promise<string> => {
  try {
    // الحصول على إعدادات ImageKit.io
    const settings = await getImageKitSettings();

    // التحقق من وجود إعدادات ImageKit.io
    if (!settings.publicKey || !settings.urlEndpoint || !settings.enabled) {
      console.error('ImageKit.io settings are incomplete or service is disabled');
      throw new Error('إعدادات ImageKit.io غير مكتملة أو الخدمة غير مفعلة');
    }

    console.log('تحميل الصورة من URL إلى ImageKit.io...');

    // إنشاء اسم الملف
    const fileName = `${Date.now()}_image_from_url`;

    // إنشاء بيانات المصادقة
    const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const expire = Math.floor(Date.now() / 1000) + 3599; // أقل من ساعة
    
    // الحصول على التوقيع من نقطة النهاية للمصادقة
    const authData = await getAuthenticationParameters(token, expire);

    // إنشاء نموذج FormData
    const formData = new FormData();
    formData.append('file', imageUrl);
    formData.append('publicKey', settings.publicKey);
    formData.append('signature', authData.signature);
    formData.append('token', authData.token);
    formData.append('expire', authData.expire);
    formData.append('fileName', fileName);
    formData.append('useUniqueFileName', 'true');

    if (folder) {
      formData.append('folder', folder);
    }

    // إرسال الطلب إلى ImageKit.io
    const response = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
      method: 'POST',
      body: formData,
    });

    // التحقق من نجاح الطلب
    if (!response.ok) {
      const errorText = await response.text();
      console.error('ImageKit.io API error response:', errorText);
      throw new Error(`فشل تحميل الصورة: ${response.status} ${response.statusText}`);
    }

    // الحصول على البيانات
    const data = await response.json();

    if (!data.url) {
      console.error('ImageKit.io API response missing URL:', data);
      throw new Error('فشل تحميل الصورة: لم يتم العثور على عنوان URL في الاستجابة');
    }

    console.log('تم تحميل الصورة بنجاح إلى ImageKit.io:', data.url);
    return data.url;
  } catch (error) {
    console.error('Error uploading image URL to ImageKit.io:', error);
    throw error;
  }
};

/**
 * تحميل مجموعة صور إلى ImageKit.io
 * @param files مصفوفة ملفات الصور المراد تحميلها
 * @param folder المجلد (اختياري)
 * @returns وعد بمصفوفة عناوين URL للصور
 */
export const uploadMultipleImagesToImageKit = async (files: File[], folder?: string): Promise<string[]> => {
  try {
    // إنشاء مصفوفة وعود لتحميل الصور
    const uploadPromises = files.map(file => uploadImageToImageKit(file, folder));

    // انتظار اكتمال تحميل جميع الصور
    const uploadedUrls = await Promise.all(uploadPromises);

    return uploadedUrls;
  } catch (error) {
    console.error('Error uploading multiple images to ImageKit.io:', error);
    throw error;
  }
};

export default {
  getImageKitSettings,
  saveImageKitSettings,
  uploadImageToImageKit,
  uploadImageUrlToImageKit,
  uploadMultipleImagesToImageKit
};
